/**
 * Round 56, D — the learner's writing-phase draft, saved locally so it
 * survives a true ungraceful kill (battery death, force-quit, OS
 * termination) where no JavaScript runs and neither the beforeunload nor
 * visibilitychange handler ever fires. Free precisely because it never
 * touches the network: no Firestore write, no quota, no per-write cost.
 *
 * One shared module so LessonPage.tsx (the three write sites: the timer,
 * beforeunload, visibilitychange) and Phase3Writing.tsx (the one clear site,
 * on successful submission) can't drift on the key format or payload shape.
 * resolveDraftContent lives here too (moved out of LessonPage.tsx, D6) so it
 * can be unit-tested without dragging in the whole page component's router/
 * hook dependencies.
 */

import type { Session } from '@/types/lesson';

export interface LocalDraftState {
  sessionId: string;
  lessonId: string;
  currentPhase: string;
  savedAt: string;
  draftText: string | null;
  authToken: string | null;
}

/** Round 129 - what the emergency-save beacon sends: ONLY the draft text, the time and the learner's own token. The session id is in the URL;
 *  the lesson and the phase are the server's to know (a client-claimed phase used to be accepted once and clobbered real progress). */
export function emergencyBeaconBody(state: LocalDraftState): { savedAt: string; draftText: string | null; authToken: string | null } {
  return { savedAt: state.savedAt, draftText: state.draftText, authToken: state.authToken };
}

const keyFor = (sessionId: string) => `emi-session-${sessionId}`;

/**
 * Round 132 (E): the browser copy NEVER holds the learner's Firebase ID token. It used to (`authToken` in this entry), where it stayed on the device after
 * sign-out. The token is only needed IN MEMORY by the emergency-save beacon (a sendBeacon cannot set an Authorization header), and that reads it from
 * `LocalDraftState` / a ref, not from storage. What is stored: the session, the lesson, the phase, the time and the draft text.
 */
export function writeLocalDraft(state: LocalDraftState): void {
  try {
    const { authToken: _inMemoryOnly, ...stored } = state;
    localStorage.setItem(keyFor(state.sessionId), JSON.stringify(stored));
  } catch {
    // localStorage unavailable or full — silently skip, the next write retries.
  }
}

/** Returns the draft text + its timestamp, or null if none exists, is
 *  corrupted, or belongs to a different session than the one asked for. */
export function readLocalDraft(sessionId: string): { text: string; savedAt: string } | null {
  try {
    const raw = localStorage.getItem(keyFor(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.sessionId !== sessionId || !parsed?.draftText) return null;
    return { text: parsed.draftText, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

const DRAFT_KEY_PREFIX = 'emi-session-';

function eachDraftKey(fn: (key: string) => void): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k && k.startsWith(DRAFT_KEY_PREFIX)) keys.push(k); }
    keys.forEach(fn);
  } catch { /* localStorage unavailable - nothing stored, nothing to do */ }
}

/** Round 132 (E): removes an `authToken` that an earlier build left in a stored draft (the draft text stays). Run once when a signed-in session starts. */
export function scrubStoredAuthTokens(): void {
  eachDraftKey((k) => {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'authToken' in parsed) { delete parsed.authToken; localStorage.setItem(k, JSON.stringify(parsed)); }
    } catch { localStorage.removeItem(k); }   // an unreadable entry is not worth keeping
  });
}

/** Round 132 (E): at sign-out every stored draft goes with the session (a shared device must not keep a learner's writing). The server keeps the last emergency save. */
export function clearAllLocalDrafts(): void {
  eachDraftKey((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
}

/** Round 56, D5 — called on successful writing submission, the one place
 *  that's supposed to fire this. Not the ONLY guarantee against a stale
 *  draft resurfacing, though: resolveDraftContent (LessonPage.tsx) also
 *  refuses to consider ANY draft once the session has moved past the
 *  writing phase, regardless of whether this call ever ran — so a clear
 *  that fails or gets skipped doesn't leave a stale draft reachable. */
export function clearLocalDraft(sessionId: string): void {
  try {
    localStorage.removeItem(keyFor(sessionId));
  } catch {
    // localStorage unavailable — nothing to do; the phase-gate in
    // resolveDraftContent still prevents this entry from resurfacing.
  }
}

/**
 * Round 56, D3 — the resume rule: prefer the local draft whenever it's at
 * least as recent as the server's, which in practice means "local if
 * present" (it writes far more often — every SAVE_COOLDOWN_MS via the timer,
 * vs. only on a beforeunload/visibilitychange beacon) — the timestamp check
 * is the safety net, not the mechanism. Deliberately returns null once the
 * writing phase itself has moved on (session.currentPhase !== 'writing'):
 * this is the guarantee against a stale draft resurfacing on an
 * already-submitted lesson that doesn't depend on the clear-on-submit call
 * (Phase3Writing.tsx, on successful submission) having actually run — even
 * if that clear somehow never fired, a draft is never even considered once
 * the phase it belongs to is done.
 */
export function resolveDraftContent(session: Session): string | null {
  if (session.currentPhase !== 'writing') return null;

  const serverSavedAt = session.draftContent ? (session.interruptedAt ?? null) : null;
  const localDraft = readLocalDraft(session.sessionId);

  if (localDraft && (!serverSavedAt || new Date(localDraft.savedAt) >= new Date(serverSavedAt))) {
    return localDraft.text;
  }
  return session.draftContent ?? null;
}
